import { useNavigate } from 'react-router-dom';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useTranslation } from 'react-i18next';

const MILESTONE_META = [
  { icon: 'ri-door-open-line', color: 'bg-teal-200 text-teal-600' },
  { icon: 'ri-award-line', color: 'bg-teal-100 text-teal-600' },
  { icon: 'ri-map-pin-2-line', color: 'bg-teal-200 text-teal-600' },
  { icon: 'ri-truck-line', color: 'bg-teal-200 text-teal-600' },
  { icon: 'ri-rocket-line', color: 'bg-teal-100 text-teal-600' },
];

export default function OurStory() {
  const leftRef = useScrollReveal<HTMLDivElement>();
  const rightRef = useScrollReveal<HTMLDivElement>();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const milestones = t('about.milestones', { returnObjects: true }) as {
    month: string;
    title: string;
    description: string;
  }[];

  return (
    <section id="our-story" className="py-24 bg-teal-50">
      <div className="max-w-7xl mx-auto px-6">

        {/* ── Section label ── */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-2 h-2 bg-teal-400 rounded-full" />
          <span className="text-gray-500 text-sm font-medium tracking-wide uppercase">{t('about.ourStoryTag')}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

          {/* ── Left: Founder narrative ── */}
          <div ref={leftRef} className="reveal-on-scroll animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
              {t('about.storyTitle1')}<br />
              <span className="text-teal-500">{t('about.storyTitle2')}</span>
            </h2>

            <div className="relative rounded-2xl overflow-hidden mb-8 w-full h-64">
              <img
                src="https://readdy.ai/api/search-image?query=bright%20modern%20office%20meeting%20room%20with%20food%20truck%20consultants%20reviewing%20documents%20and%20blueprints%20on%20a%20large%20table%2C%20natural%20daylight%20streaming%20through%20large%20windows%2C%20clean%20professional%20workspace%20with%20white%20walls%20and%20green%20plants%2C%20collaborative%20team%20discussion%20around%20paperwork%2C%20minimalist%20corporate%20interior%20design&width=640&height=320&seq=story3&orientation=landscape"
                alt="OCFTA team reviewing food truck plans in bright modern office"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
              <div className="absolute bottom-4 left-5 flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-teal-500 text-white text-sm">
                  <i className="ri-store-2-line" />
                </div>
                <span className="text-white text-sm font-semibold">Orange County, CA</span>
              </div>
            </div>

            <p className="text-gray-600 text-base leading-relaxed mb-5">{t('about.paragraph1')}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-5">{t('about.paragraph2')}</p>
            <p className="text-gray-600 text-base leading-relaxed mb-8">{t('about.paragraph3')}</p>

            <div className="flex items-center gap-4 p-5 bg-white rounded-2xl">
              <div className="w-14 h-14 flex items-center justify-center rounded-full overflow-hidden flex-shrink-0">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20business%20consultant%20portrait%20in%20modern%20bright%20office%20setting%2C%20wearing%20smart%20casual%20attire%20with%20a%20friendly%20confident%20expression%2C%20natural%20window%20lighting%2C%20clean%20white%20background%20with%20soft%20greenery%2C%20corporate%20headshot%20style%20warm%20and%20approachable&width=112&height=112&seq=founder3&orientation=squarish"
                  alt="OCFTA Founder in bright office"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div>
                <div className="font-bold text-gray-900 text-sm">{t('about.founderName')}</div>
                <div className="text-gray-400 text-xs mt-0.5">{t('about.founderRole')}</div>
                <div className="flex gap-1 mt-2">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <i key={s} className="ri-star-fill text-teal-400 text-xs" />
                  ))}
                  <span className="text-gray-400 text-xs ml-1">{t('about.founderYears')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Milestone timeline ── */}
          <div ref={rightRef} className="reveal-on-scroll animate-fade-in-up">
            <h3 className="text-xl font-bold text-gray-900 mb-8">
              {t('about.milestonesTitle')}
            </h3>

            <div className="relative">
              {/* Vertical line */}
              <div className="absolute left-5 top-0 bottom-0 w-px bg-teal-100" />

              <div className="flex flex-col gap-0">
                {milestones.map((m, i) => (
                  <div key={m.month} className="relative flex gap-6 pb-8 last:pb-0">
                    {/* Icon dot */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full ${MILESTONE_META[i].color} text-lg`}>
                        <i className={MILESTONE_META[i].icon} />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="bg-white rounded-2xl p-5 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-bold text-teal-500 uppercase tracking-wider">
                          {m.month}
                        </span>
                        {i === milestones.length - 1 && (
                          <span className="bg-teal-500 text-white text-xs font-semibold px-2 py-0.5 rounded-full">
                            {t('about.today')}
                          </span>
                        )}
                      </div>
                      <h4 className="font-bold text-gray-900 text-sm mb-1">{m.title}</h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{m.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── CTA ── */}
        <div className="mt-16 text-center">
          <p className="text-gray-500 text-sm mb-4">{t('about.ctaText')}</p>
          <button
            onClick={() => navigate('/#consultation')}
            className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white font-semibold text-sm px-8 py-4 rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap"
          >
            {t('about.bookSession')}
            <i className="ri-arrow-right-line" />
          </button>
        </div>

      </div>
    </section>
  );
}